// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Shader from '../../renderer/Shader';
import ShaderBuilder from '../../renderer/shaders/ShaderBuilder';
import MeshData from '../../renderer/MeshData';

class ParticleShaderLib {
}
ParticleShaderLib.particle = {
  name:"ParticleShaderLib.particle",
  glslversion:300,
  defines: {
    START_LIFETIME_CODE: '5.0',
    START_SIZE_CODE: '1.0',
    START_ROTATION_CURVE_CODE: '0.0',
    START_COLOR_CODE: 'vec4(1.0)',
    SIZE_CURVE_CODE: '1.0',
    ROTATION_CURVE_CODE: '0.0',
    COLOR_CURVE_CODE: 'vec4(1.0)',
    VELOCITY_CURVE_CODE: 'vec3(0.0)',
    WORLD_VELOCITY_CURVE_CODE: 'vec3(0.0)',
    TEXTURE_FRAME_CODE: 't',
    INSTANCE_COUNT: '1',
  },
  processors: [
    ShaderBuilder.uber.fog,
  ],
  attributes: {
    vertexPosition: MeshData.POSITION,
    vertexUV0: MeshData.TEXCOORD0,
  },
  uniforms: {
    viewMatrix: Shader.VIEW_MATRIX,
    projectionMatrix: Shader.PROJECTION_MATRIX,
    viewProjectionMatrix: Shader.VIEW_PROJECTION_MATRIX,
    worldMatrix: Shader.WORLD_MATRIX,
    cameraPosition: Shader.CAMERA,

    textureTileInfo: [1, 1, 1, 0],
    invWorldRotation: [1, 0, 0, 0, 1, 0, 0, 0, 1],
    worldRotation: [1, 0, 0, 0, 1, 0, 0, 0, 1],
    particleTexture: 'PARTICLE_TEXTURE',
    time: 0,
    duration: 5,
    gravity: [0, 0, 0],
    discardThreshold: 0,

    // Uniforms for scaling the curves
    uColor: [1, 1, 1, 1],
    uStartSize: 1,
    uStartAngle: 1,
    uRotationSpeed: 1,

    fogSettings: [0, 10000],
    fogColor: [1, 1, 1],
    startPosInstances: Shader.INSTANCED_UNIFORM_DATA,
    startDirInstances: Shader.INSTANCED_UNIFORM_DATA,
    timeInfoInstances: Shader.INSTANCED_UNIFORM_DATA,
  },
  vshader: `
        #ifdef WEBGL_1
          #define in attribute
          #define out varying
        #endif

        in vec3 vertexPosition;
        in vec2 vertexUV0;
        
        #ifdef WEBGL_1 // INSTANCED
          in mat4 worldMatrix;
          in vec4 timeInfoInstances;
          in vec4 startPosInstances;
          in vec4 startDirInstances;
        #else	//WEBGL_2 INSTANCED  
          uniform uniformBlockVS
          {
           mat4 worldMatrix[INSTANCE_COUNT];
           vec4 timeInfoInstances[INSTANCE_COUNT];
           vec4 startPosInstances[INSTANCE_COUNT];
           vec4 startDirInstances[INSTANCE_COUNT];
	  };
	#endif

        uniform vec4 textureTileInfo;
        uniform mat4 viewMatrix;
        uniform mat4 projectionMatrix;
        uniform mat4 viewProjectionMatrix;
        uniform mat3 invWorldRotation;
        uniform mat3 worldRotation;
        uniform vec3 cameraPosition;
        uniform float time;
        uniform float duration;
        uniform vec3 gravity;

        uniform vec4 uColor;
        uniform float uStartSize;
        uniform float uStartAngle;
        uniform float uRotationSpeed;

        #ifdef FOG
          uniform vec2 fogSettings;
          uniform vec3 fogColor;
        #endif

        out vec4 color;
        out vec2 coords;

        vec3 getVelocityCurveIntegral(float t, float emitRandom) {
            return VELOCITY_CURVE_CODE;
        }

        vec3 getWorldVelocityCurveIntegral(float t, float emitRandom) {
            return WORLD_VELOCITY_CURVE_CODE;
        }

        vec3 getPosition(mat3 invWorldRotation, mat3 worldRotation, float t, vec3 pos, vec3 vel, vec3 g, float emitRandom, float duration) {
            return pos + vel * t + 0.5 * t * t * g + worldRotation * getVelocityCurveIntegral(t / duration, emitRandom) + invWorldRotation * getWorldVelocityCurveIntegral(t / duration, emitRandom);
        }

        float getScale(float t, float emitRandom) {
            return SIZE_CURVE_CODE;
        }

        float getStartSize(float t, float emitRandom) {
            return START_SIZE_CODE;
        }

        float getTextureFrame(float t, float emitRandom) {
            return TEXTURE_FRAME_CODE;
        }

        float getAngle(float t, float emitRandom) {
            return ROTATION_CURVE_CODE;
        }

        vec4 getColor(float t, float emitRandom) {
            return COLOR_CURVE_CODE;
        }

        vec4 getStartColor(float t, float emitRandom) {
            return START_COLOR_CODE;
        }

        float getStartAngle(float t, float emitRandom) {
            return START_ROTATION_CURVE_CODE;
        }

        mat4 rotationMatrix(vec3 axis, float angle) {
            axis = normalize(axis);
            float s = sin(angle);
            float c = cos(angle);
            float x = axis.x;
            float y = axis.y;
            float z = axis.z;
            float oc = 1.0 - c;
            return mat4(oc * x * x + c, oc * x * y - z * s,  oc * z * x + y * s,  0.0,
            oc * x * y + z * s, oc * y * y + c, oc * y * z - x * s, 0.0,
            oc * z * x - y * s, oc * y * z + x * s,  oc * z * z + c, 0.0,
            0.0, 0.0, 0.0, 1.0);
        }

        void main(void) {
	    #ifdef WEBGL_1 // INSTANCED
		mat4 wMatrix = worldMatrix;
		vec4 timeInfo = timeInfoInstances;
		vec4 startPos = startPosInstances;
		vec4 startDir = startDirInstances;

	    #else // WEBGL_2 INSTANCED
		mat4 wMatrix = worldMatrix[gl_InstanceID];
		vec4 timeInfo = timeInfoInstances[gl_InstanceID];
		vec4 startPos = startPosInstances[gl_InstanceID];
		vec4 startDir = startDirInstances[gl_InstanceID];
	    #endif

            float isActive = timeInfo.y;
            float emitTime = timeInfo.w;
            float age = time - emitTime;
            float ageNoMod = age;
            float loopAfter = startDir.w;

            float unitEmitTime = mod(emitTime / duration, 1.0);
            float emitRandom = timeInfo.z;
            float startSize = uStartSize * getStartSize(unitEmitTime, emitRandom);
            float lifeTime = timeInfo.x;
            float startAngle = uStartAngle * getStartAngle(unitEmitTime, emitRandom);

            float unitAge = age / lifeTime;
            color = uColor * getStartColor(unitAge, emitRandom) * getColor(unitAge, emitRandom);

            float textureAnimationCycles = textureTileInfo.z;
            float tileX = floor(mod(textureTileInfo.x * textureTileInfo.y * getTextureFrame(unitAge, emitRandom) * textureAnimationCycles, textureTileInfo.x));
            float tileY = floor(mod(-textureTileInfo.y * getTextureFrame(unitAge, emitRandom) * textureAnimationCycles, textureTileInfo.y));
            vec2 texOffset = vec2(tileX, tileY) / textureTileInfo.xy;
            coords = (vertexUV0 / textureTileInfo.xy + texOffset);

            float rotation = uRotationSpeed * getAngle(unitAge, emitRandom) + startAngle;
            float c = cos(rotation);
            float s = sin(rotation);
            mat3 spinMatrix = mat3(c, s, 0, -s, c, 0, 0, 0, 1);

            // hide if age > lifeTime
            isActive *= step(-lifeTime, -age);

            // hide if age < 0
            #ifdef HIDE_IF_EMITTED_BEFORE_ZERO
              isActive *= step(0.0, ageNoMod) * step(0.0, age);
            #endif

            vec3 position = getPosition(invWorldRotation, worldRotation, age, startPos.xyz, startDir.xyz, gravity, emitRandom, duration);

            #ifdef FOG
              vec3 viewPosition = cameraPosition - (wMatrix * vec4(position, 0.0)).xyz;
              float d = pow(smoothstep(fogSettings.x, fogSettings.y, length(viewPosition)), 1.0);
              color.rgb = mix(color.rgb, fogColor, d);
            #endif

            #ifdef BILLBOARD
              vec2 offset = ((spinMatrix * vertexPosition)).xy * startSize * getScale(unitAge, emitRandom) * isActive;
              mat4 matPos = wMatrix * mat4(vec4(0),vec4(0),vec4(0),vec4(position,0));
              gl_Position = viewProjectionMatrix * (wMatrix + matPos) * vec4(0, 0, 0, 1) + projectionMatrix * vec4(offset.xy, 0, 0);
            #else
              mat4 rot = rotationMatrix(normalize(vec3(sin(emitTime*5.0),cos(emitTime*1234.0),sin(emitTime))),rotation);
              gl_Position = viewProjectionMatrix * wMatrix * (rot * vec4(startSize * getScale(unitAge, emitRandom) * isActive * vertexPosition, 1.0) + vec4(position,0.0));
            #endif
        }`,
  fshader: `
      #ifdef WEBGL_1
        #define in varying
      #endif

        uniform sampler2D particleTexture;
        uniform float discardThreshold;

        in vec4 color;
        in vec2 coords;
        
      #ifdef WEBGL_1
        #undef in
        #define fragColor gl_FragColor
        #define texture texture2D
      #else
        out vec4 fragColor;
      #endif

        void main(void) {
        #ifdef PARTICLE_TEXTURE
            vec4 col = color * texture(particleTexture, coords);
        #else
            vec4 col = color;
        #endif
            if (col.a < discardThreshold) discard;
            fragColor = col;
        }`,
};
export default ParticleShaderLib;
